<!-- src/apps/workspace/account/settings/SecurityOverview.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import SettingsLayout from '@/apps/workspace/layouts/SettingsLayout.vue';
  import { useAccount } from '@/shared/composables/useAccount';
  import { hasPasswordOf, isMfaEnabledOf, isWebAuthnEnabledOf } from '@/utils/features';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import type { AccountInfo } from '@/types/auth';
  import { computed, onMounted, ref } from 'vue';

  const { t } = useI18n();
  const bootstrapStore = useBootstrapStore();

  // Feature toggles — derived from the reactive bootstrap store so they reflect
  // post-login state without re-mounting (e.g. after checkWindowStatus refresh).
  const showSessionsCard = ref(false);
  const mfaFeatureEnabled = computed(() => isMfaEnabledOf(bootstrapStore));
  const webAuthnEnabled = computed(() => isWebAuthnEnabledOf(bootstrapStore));
  const hasPw = computed(() => hasPasswordOf(bootstrapStore));
  const { accountInfo, fetchAccountInfo } = useAccount();

  interface SecurityCard {
    id: string;
    icon: { collection: string; name: string };
    title: string;
    description: string;
    status: 'active' | 'inactive' | 'warning';
    statusText: string;
    action: {
      label: string;
      to: string;
    };
  }

  const securityScore = computed(() => {
    if (!accountInfo.value) return 0;

    if (mfaFeatureEnabled.value) {
      let score = 0;
      if (accountInfo.value.email_verified) score += 25;
      if (accountInfo.value.mfa_enabled) score += 50;
      if (accountInfo.value.recovery_codes_count > 0) score += 25;
      return score;
    }

    // When MFA is disabled, score based on available factors only
    return accountInfo.value.email_verified ? 100 : 0;
  });

  const securityLevel = computed(() => {
    const score = securityScore.value;
    if (score >= 90) return { label: t('web.settings.security.excellent'), color: 'green' };
    if (score >= 70) return { label: t('web.settings.security.good'), color: 'blue' };
    if (score >= 25) return { label: t('web.settings.security.fair'), color: 'yellow' };
    return { label: t('web.settings.security.weak'), color: 'red' };
  });

  // Helper to build core security cards
  function buildCoreCards(info: AccountInfo): SecurityCard[] {
    return [
      {
        id: 'password',
        icon: { collection: 'heroicons', name: 'lock-closed-solid' },
        title: t('web.auth.change_password.title'),
        description: t('web.settings.password.update_account_password'),
        status: 'active',
        statusText: t('web.settings.security.configured'),
        action: {
          label: t('web.settings.security.change'),
          to: '/account/settings/security/password',
        },
      },
      {
        id: 'mfa',
        icon: { collection: 'heroicons', name: 'key-solid' },
        title: t('web.auth.mfa.title'),
        description: t('web.auth.mfa.setup_description'),
        status: info.mfa_enabled ? 'active' : 'warning',
        statusText: info.mfa_enabled
          ? t('web.auth.account.mfa_enabled')
          : t('web.auth.account.mfa_disabled'),
        action: {
          label: info.mfa_enabled ? t('web.settings.security.manage') : t('web.settings.security.enable'),
          to: '/account/settings/security/mfa',
        },
      },
      {
        id: 'recovery-codes',
        icon: { collection: 'heroicons', name: 'document-text-solid' },
        title: t('web.auth.recovery_codes.title'),
        description: t('web.auth.recovery_codes.description'),
        status: info.recovery_codes_count > 0 ? 'active' : 'inactive',
        statusText:
          info.recovery_codes_count > 0
            ? t('web.settings.security.codes_available', [info.recovery_codes_count])
            : t('web.settings.security.no_codes'),
        action: {
          label: t('web.settings.security.manage'),
          to: '/account/settings/security/recovery-codes',
        },
      },
    ];
  }

  // Helper to build passkey card
  function buildPasskeyCard(info: AccountInfo): SecurityCard {
    const passkeyCount = info.passkeys_count ?? 0;
    return {
      id: 'passkeys',
      icon: { collection: 'heroicons', name: 'finger-print-solid' },
      title: t('web.auth.passkeys.title'),
      description: t('web.auth.passkeys.description'),
      status: passkeyCount > 0 ? 'active' : 'inactive',
      statusText:
        passkeyCount > 0
          ? t('web.auth.passkeys.count', { count: passkeyCount }, passkeyCount)
          : t('web.auth.passkeys.not_configured'),
      action: {
        label: passkeyCount > 0 ? t('web.settings.security.manage') : t('web.settings.security.enable'),
        to: '/account/settings/security/passkeys',
      },
    };
  }

  // Helper to build sessions card
  function buildSessionsCard(info: AccountInfo): SecurityCard {
    return {
      id: 'sessions',
      icon: { collection: 'heroicons', name: 'computer-desktop-solid' },
      title: t('web.auth.sessions.title'),
      description: t('web.settings.sessions.manage_active_sessions'),
      status: 'active',
      statusText: t('web.settings.security.active_sessions', [info.active_sessions_count || 1]),
      action: {
        label: t('web.settings.security.manage'),
        to: '/account/settings/security/sessions',
      },
    };
  }

  const PASSWORD_DEPENDENT_CARDS = new Set(['password', 'mfa', 'recovery-codes']);

  const securityCards = computed<SecurityCard[]>(() => {
    if (!accountInfo.value) return [];

    let cards = buildCoreCards(accountInfo.value);

    if (!hasPw.value) {
      cards = cards.filter((c) => !PASSWORD_DEPENDENT_CARDS.has(c.id));
    }

    // Hide MFA and recovery codes cards when MFA feature is disabled
    if (!mfaFeatureEnabled.value) {
      cards = cards.filter((c) => c.id !== 'mfa' && c.id !== 'recovery-codes');
    }

    if (webAuthnEnabled.value) {
      cards.push(buildPasskeyCard(accountInfo.value));
    }

    if (showSessionsCard.value) {
      cards.push(buildSessionsCard(accountInfo.value));
    }

    return cards;
  });

  const statusColorClasses = {
    active: 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/20 dark:text-green-400',
    inactive: 'bg-gray-50 text-gray-600 ring-gray-500/20 dark:bg-gray-900/20 dark:text-gray-400',
    warning:
      'bg-yellow-50 text-yellow-800 ring-yellow-600/20 dark:bg-yellow-900/20 dark:text-yellow-400',
  };

  const scoreColorClasses: Record<string, string> = {
    green: 'text-green-600 dark:text-green-400',
    blue: 'text-blue-600 dark:text-blue-400',
    yellow: 'text-yellow-600 dark:text-yellow-400',
    red: 'text-red-600 dark:text-red-400',
  };

  const progressBarColorClasses: Record<string, string> = {
    green: 'bg-green-600',
    blue: 'bg-blue-600',
    yellow: 'bg-yellow-600',
    red: 'bg-red-600',
  };

  onMounted(async () => {
    await fetchAccountInfo();
  });
</script>

<template>
  <SettingsLayout>
    <div class="space-y-8">
      <!-- Security Score Card -->
      <div
        v-if="false"
        class="rounded-lg border border-gray-200/60 bg-white/60 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700/60 dark:bg-gray-800/60">
        <div class="flex items-start justify-between">
          <div>
            <h2 class="text-lg font-medium text-gray-600 dark:text-gray-300">
              {{ t('web.settings.security.security_score') }}
            </h2>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {{ t('web.settings.security.score_description') }}
            </p>
          </div>
          <div class="text-right">
            <div :class="['text-4xl font-bold', scoreColorClasses[securityLevel.color]]">
              {{ securityScore }}
            </div>
            <div
              class="mt-1 text-sm font-medium"
              :class="scoreColorClasses[securityLevel.color]">
              {{ securityLevel.label }}
            </div>
          </div>
        </div>

        <!-- Progress Bar -->
        <div class="mt-4">
          <div class="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              :class="[
                'h-full transition-all duration-500',
                progressBarColorClasses[securityLevel.color],
              ]"
              :style="{ width: `${securityScore}%` }"></div>
          </div>
        </div>

        <!-- Recommendations -->
        <div
          v-if="securityScore < 100"
          class="mt-4 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
          <div class="flex gap-3">
            <OIcon
              collection="heroicons"
              name="information-circle-solid"
              class="size-5 shrink-0 text-blue-600 dark:text-blue-400"
              aria-hidden="true" />
            <div class="text-sm text-blue-700 dark:text-blue-300">
              <p class="font-medium">
                {{ t('web.settings.security.improve_security') }}
              </p>
              <ul class="mt-2 list-inside list-disc space-y-1">
                <li v-if="!accountInfo?.mfa_enabled">
                  {{ t('web.settings.security.enable_mfa_recommendation') }}
                </li>
                <li v-if="!accountInfo?.recovery_codes_count">
                  {{ t('web.settings.security.generate_recovery_codes_recommendation') }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- SSO empty state — shown when all cards are filtered out -->
      <div
        v-if="!hasPw && securityCards.length === 0"
        class="rounded-lg border border-gray-200/60 bg-white/60 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700/60 dark:bg-gray-800/60">
        <div class="flex items-start gap-4">
          <div
            class="flex size-12 shrink-0 items-center justify-center rounded-lg bg-brand-50 dark:bg-brand-900/20">
            <OIcon
              collection="heroicons"
              name="shield-check-solid"
              class="size-6 text-brand-600 dark:text-brand-400"
              aria-hidden="true" />
          </div>
          <div>
            <h3 class="text-base font-medium text-gray-900 dark:text-white">
              {{ t('web.settings.security.sso_managed_title') }}
            </h3>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {{ t('web.settings.security.sso_managed_description') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Security Settings Cards -->
      <div
        v-if="securityCards.length > 0"
        class="grid gap-6 sm:grid-cols-2">
        <div
          v-for="card in securityCards"
          :key="card.id"
          class="rounded-lg border border-gray-200/60 bg-white/60 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700/60 dark:bg-gray-800/60">
          <div class="flex items-start gap-4">
            <div
              class="flex size-12 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
              <OIcon
                :collection="card.icon.collection"
                :name="card.icon.name"
                class="size-6 text-gray-600 dark:text-gray-400"
                aria-hidden="true" />
            </div>
            <div class="flex-1">
              <h3 class="text-base font-medium text-gray-900 dark:text-white">
                {{ card.title }}
              </h3>
              <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {{ card.description }}
              </p>

              <!-- Status Badge -->
              <div class="mt-3">
                <span
                  :class="[
                    'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset',
                    statusColorClasses[card.status],
                  ]">
                  {{ card.statusText }}
                </span>
              </div>

              <!-- Action Button -->
              <div class="mt-4">
                <router-link
                  :to="card.action.to"
                  class="inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300">
                  {{ card.action.label }}
                  <OIcon
                    collection="heroicons"
                    name="arrow-right-solid"
                    class="size-4"
                    aria-hidden="true" />
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SettingsLayout>
</template>
